
import { defineComponent, ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { useRoute } from 'vue-router'
import { initIM, loginIMWithPassword, sendPrivateMessage } from '@/utils/im'
import { emojiMap, emojiList, emojiToTextMap } from '@/utils/emoji'
import chatApi from '@/api/chat'
import moment from 'moment'
import { message as antMessage } from 'ant-design-vue'
import { SmileOutlined, PictureOutlined } from '@ant-design/icons-vue'
import FdLoading from '@/components/FdLoading.vue'
import { getParameterValue } from '../../services/commonFunction'
import { zh_tran } from '@/utils/lang'

// Shorten global message duration to 1.5s
antMessage.config({
  duration: 1.5
})

interface ChatMessage {
  id?: string
  serverMsgId?: string
  // 后端用于去重的引用 ID，例如 getSendMsg 返回的 messages[].reply_to.id
  replyToId?: string | number
  from: string
  to: string
  msg: string
  time: number
  isRecalled?: boolean
  type?: string
  url?: string
  isMe?: boolean
  avatar?: string
  isRead?: boolean
}

interface ChatUser {
    username: string
    nickname?: string
    avatar?: string
    lastMessage?: string
    time?: number
    unreadCount?: number
    suid: string
    last_msg_id?: string
    easemob_name?: string // easemob 用户名，用于匹配消息的 to 字段
    historyLoaded?: boolean // 是否已拉取过历史消息，避免未选中时 ws 重复
}

export default defineComponent({
  name: 'Chat',
  components: {
    SmileOutlined,
    PictureOutlined,
    FdLoading
  },
  setup() {
    const route = useRoute()
    const username = ref('')
    const password = ref('')
    const permissionType = ref<number>(0) // 0: None, 1: User, 2: Teacher
    const targetUser = ref('') // The person we are chatting with
    
    // 从路由获取初始用户名（作为默认值）
    const initialUserName = (route.query.userName as string) || (route.params.userName as string) || ''
    
    // 当前聊天对象名称，根据权限类型动态显示
    const currentUserName = computed(() => {
      // 老师模式：显示当前选中的用户
      if (permissionType.value === 2) {
        if (activeChat.value) {
          return activeChat.value.nickname || activeChat.value.username || ''
        }
        return initialUserName || ''
      }
      // 用户模式：显示对方（老师）的名字
      else if (permissionType.value === 1) {
        return currentChatUserInfo.value.nickname || currentChatUserInfo.value.user_name || currentChatUserInfo.value.name || initialUserName || ''
      }
      return initialUserName || ''
    })
    
    const isConnected = ref(false)
    const inputText = ref('')
    const messages = ref<ChatMessage[]>([])
    const messageListRef = ref<HTMLElement | null>(null)
    // 记录已出现的消息 ID（serverMsgId / id），用于全局去重
    const seenMessageIds = new Set<string>()
    // 多会话消息池：key 为会话标识（教师模式下为学生 easemob_name），value 为该会话的消息列表
    const messageMap = ref<Record<string, ChatMessage[]>>({})
    // WS 首次推送但该会话历史尚未拉取时的临时缓存，避免与 getChats 的未读重复叠加
    const wsPendingMap = ref<Record<string, ChatMessage[]>>({})
    const sending = ref(false)
    const textareaRef = ref<any>(null)
    
    const isLoadingHistory = ref(false)
    const hasMoreHistory = ref(true)
    const offlineIcon = require('@/assets/deepLoadingFd.png')
    const initLoading = ref(true)
    const fileInput = ref<HTMLInputElement | null>(null)
    const showEmoji = ref(false)
    const isKickedOffline = ref(false)
    const languageTraditional = ref(false)
    
    const chatList = ref<ChatUser[]>([])
    const activeChat = ref<ChatUser | null>(null)
    const currentUserInfo = ref<any>({})
    const currentChatUserInfo = ref<any>({})
    // 判断两个列表项是否代表同一会话（用于去重、合并未读）
    const isSameChatItem = (a?: ChatUser | null, b?: ChatUser | null) => {
      if (!a || !b) return false
      const aSuid = a.suid != null ? String(a.suid) : ''
      const bSuid = b.suid != null ? String(b.suid) : ''
      const aEase = a.easemob_name || ''
      const bEase = b.easemob_name || ''
      const aUser = a.username || ''
      const bUser = b.username || ''
      return (
        (!!aSuid && !!bSuid && aSuid === bSuid) ||
        (!!aEase && !!bEase && aEase === bEase) ||
        (!!aUser && !!bUser && aUser === bUser)
      )
    }

    // 去重 chatList 中同一会话的多份记录，保留目标项，并可选择清零未读
    const dedupeChatListFor = (target: ChatUser, clearUnread: boolean) => {
      const result: ChatUser[] = []
      let kept = false
      let mergedUnread = 0
      chatList.value.forEach(item => {
        if (isSameChatItem(item, target)) {
          mergedUnread += item.unreadCount || 0
          if (!kept) {
            kept = true
            const cloned = { ...item }
            if (clearUnread) {
              cloned.unreadCount = 0
            } else {
              cloned.unreadCount = mergedUnread
            }
            result.push(cloned)
          }
        } else {
          result.push(item)
        }
      })
      chatList.value = result
    }
    // 判断列表项是否为当前激活会话，避免未定义标识导致全部高亮
    const isActiveChat = (item: ChatUser) => {
      const active = activeChat.value
      if (!active) return false
      if (active.easemob_name && item.easemob_name && active.easemob_name === item.easemob_name) return true
      if (active.suid && item.suid && active.suid === item.suid) return true
      if (active.username && item.username && active.username === item.username) return true
      return false
    }

    // 根据是否本人，返回对应的昵称显示
    const getDisplayName = (msg: ChatMessage) => {
      if (msg.isMe) {
        return currentUserInfo.value.nickname || currentUserInfo.value.user_name || currentUserInfo.value.name || msg.from
      }
      return currentChatUserInfo.value.nickname || currentChatUserInfo.value.user_name || currentChatUserInfo.value.name || msg.from
    }

    // 发送后更新列表预览（仅老师模式，不再改变用户列表顺序）
    const updateChatListAfterSend = (type: string, content: string) => {
      if (permissionType.value !== 2 || !activeChat.value) {
        return
      }
      const preview = type === 'img' ? '[图片]' : content
      const newTime = Date.now()
      
      // 直接更新 activeChat（由于 selectChat 中已经确保 activeChat 指向 chatList 中的对象，所以这里更新会自动反映到列表中）
      activeChat.value.lastMessage = preview
      activeChat.value.time = newTime
    }

    // 会话 key 统一：优先 easemob_name，其次 suid，再次 username，避免 messageMap 错配
    const normalizeChatKey = (easemob?: string, suid?: string | number, username?: string) => {
      if (easemob) return easemob
      if (suid !== undefined && suid !== null && suid !== '') return String(suid)
      if (username) return username
      return ''
    }
    const getChatKeyFromUser = (user: ChatUser) =>
      normalizeChatKey(user.easemob_name, user.suid, user.username)

    // 从历史接口 user/to_user & to_suid 计算会话 key
    const getChatKeyFromHistory = (toUserData: any, to_suid: number | string) =>
      normalizeChatKey(
        toUserData.easemob_username || toUserData.easemob_name,
        to_suid,
        toUserData.username
      )

    // 从环信消息计算会话 key（教师模式：msg.from = 学生 easemob_name）
    const getChatKeyFromWs = (msg: any) => {
      if (permissionType.value === 2) {
        return normalizeChatKey(msg.from, undefined, msg.from)
      }
      // 学生模式只有一个会话
      return 'single'
    }

    // 预览文案：根据消息类型生成列表预览
    const getPreviewText = (msg: ChatMessage) => {
      if (!msg) return ''
      // 撤回消息在列表中展示为“你/对方撤回了一条消息”
      if (msg.isRecalled) {
        return msg.isMe ? '你撤回了一条消息' : '对方撤回了一条消息'
      }
      if (msg.type === 'img') return '[图片]'
      if (msg.type === 'video') return '[视频]'
      if (msg.type === 'audio') return '[语音]'
      if (msg.type === 'pdf') return '[文件]'
      const content = msg.msg || ''
      // Win7 等低版本在列表预览中用文本占位替代 emoji
      const needTextFallback = useEmojiImage.value || !supportsEmoji.value
      return needTextFallback ? convertEmojiToText(content) : content
    }

    // 列表预览使用“真正的最后一条消息”（包括撤回），保证和聊天框底部一致
    const getLatestForPreview = (list: ChatMessage[]) => {
      if (!list || !list.length) return null
      return list[list.length - 1]
    }

    // 将会话消息列表同步到 chatList 预览：
    // 直接使用最后一条消息（可能是撤回），与聊天框显示保持一致
    const syncChatPreviewFromList = (chatKey: string, list: ChatMessage[]) => {
      if (!chatKey) return
      const chat = chatList.value.find(
        c => c.easemob_name === chatKey || c.suid === chatKey || c.username === chatKey
      )
      if (!chat) return
      const last = getLatestForPreview(list)
      chat.lastMessage = last ? getPreviewText(last) : ''
      chat.time = last ? last.time : chat.time
    }


    // 低版本环境（如早期 Windows）可能不支持彩色 emoji
    const supportsEmoji = ref(true)
    // Windows 7 需要使用图片替代 emoji 字符
    const useEmojiImage = ref(false)

    const contextMenu = ref({
        visible: false,
        x: 0,
        y: 0,
        targetMsg: null as ChatMessage | null,
        targetEl: null as HTMLElement | null
    })
    const contextMenuRef = ref<HTMLElement | null>(null)
    const contextMenuTimer = ref<number | null>(null)

    // 统一的 emoji 匹配正则（用于图片或文本回退）
    const emojiRegex = /[\uD83C][\uDF00-\uDFFF]|[\uD83D][\uDC00-\uDE4F]|[\uD83D][\uDE80-\uDEFF]|[\u2600-\u26FF]|[\u2700-\u27BF]|[\uD83E][\uDD00-\uDDFF]/g

    // 将 emoji 字符转换为图片 URL（使用 Twemoji CDN）
    const emojiToImage = (emoji: string): string => {
        // 将 emoji 字符转换为 Unicode 码点
        const codePoint = emoji.codePointAt(0)?.toString(16)
        if (!codePoint) return emoji
        // 使用 Twemoji CDN（Twitter 开源的 emoji 图片库）
        return `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codePoint}.svg`
    }

    // 将文本中的 emoji 字符转换为图片 HTML
    const convertEmojiToImage = (text: string): string => {
        return text.replace(emojiRegex, (emoji) => {
            // 处理代理对（surrogate pairs）
            let codePoint: number | undefined
            if (emoji.length === 2) {
                // 代理对
                const high = emoji.charCodeAt(0)
                const low = emoji.charCodeAt(1)
                codePoint = (high - 0xD800) * 0x400 + (low - 0xDC00) + 0x10000
            } else {
                codePoint = emoji.codePointAt(0)
            }
            if (!codePoint) return emoji
            const codePointHex = codePoint.toString(16)
            // 转义 HTML 特殊字符（用于 alt 属性）
            const safeEmoji = emoji.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
            return `<img src="https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codePointHex}.svg" alt="${safeEmoji}" class="emoji-img" style="width: 20px; height: 20px; vertical-align: middle; display: inline-block;" />`
        })
    }

    // 将 emoji 转换为文本占位（如 [微笑]），用于不支持彩色 emoji 的列表预览
    const convertEmojiToText = (text: string): string => {
      if (!text) return ''
      return text.replace(emojiRegex, (emoji) => emojiToTextMap[emoji] || '[emoji]')
    }

    const parseEmoji = (msg: string) => {
        if (!msg) return ''
        // 先将 [文本] 格式转换为 emoji 字符
        let result = msg.replace(/\[(.*?)\]/g, (match) => {
            return emojiMap[match] || match
        })
        
        // 如果是不支持 emoji 的环境，直接返回原文本
        if (!supportsEmoji.value) return result
        
        // 如果是 Windows 7，将 emoji 字符转换为图片
        if (useEmojiImage.value) {
            return convertEmojiToImage(result)
        }
        
        return result
    }

    const isTeacherMessage = (msg: ChatMessage) => {
        return (permissionType.value === 2 && msg.isMe) || (permissionType.value === 1 && !msg.isMe)
    }

    // Close context menu on global click
    const closeContextMenu = () => {
        // 清除定时器
        if (contextMenuTimer.value) {
            clearTimeout(contextMenuTimer.value)
            contextMenuTimer.value = null
        }
        contextMenu.value.visible = false
        contextMenu.value.targetMsg = null
        contextMenu.value.targetEl = null
    }

    const applyLocaleTransform = () => {
        if (!languageTraditional.value) return
        nextTick(() => zh_tran('t'))
    }

    // 撤回时更新会话列表预览
    const updateChatPreviewOnRecall = (msg: ChatMessage) => {
        if (!msg) return
        const chatKey = msg.isMe ? msg.to : msg.from
        const chat = chatList.value.find(
            c => c.suid == chatKey || c.username == chatKey || c.easemob_name == chatKey
        )
        if (!chat) return
        // 查找该会话消息列表（优先 messageMap，其次当前 messages 作为兜底）
        const list =
          (permissionType.value === 2 && chatKey && messageMap.value[chatKey]) ||
          messages.value
        const latest = getLatestForPreview(list)
        chat.lastMessage = latest ? getPreviewText(latest) : ''
        chat.time = latest ? latest.time : chat.time
    }

    // 根据消息元素定位菜单，使其跟随该消息（在滚动时也保持位置）
    const updateContextMenuPosition = () => {
        if (!contextMenu.value.visible || !contextMenu.value.targetEl) return
        const el = contextMenu.value.targetEl
        const rect = el.getBoundingClientRect()
        const menuEl = contextMenuRef.value
        const menuWidth = menuEl ? menuEl.getBoundingClientRect().width : 120 // 与 min-width 对齐的兜底
        const menuHeight = menuEl ? menuEl.getBoundingClientRect().height : 40 // 预估菜单高度
        
        // 获取输入框的位置
        const inputAreaEl = document.querySelector('.input-area-wrapper') as HTMLElement
        const inputAreaRect = inputAreaEl ? inputAreaEl.getBoundingClientRect() : null
        
        // 获取发送按钮的位置（用于检查水平重叠）
        const sendBtnEl = document.querySelector('.send-btn') as HTMLElement
        const sendBtnRect = sendBtnEl ? sendBtnEl.getBoundingClientRect() : null
        
        // 如果输入框存在，检查消息是否被输入框覆盖
        if (inputAreaRect) {
            // 如果消息的底部已经进入输入框区域，说明消息被输入框覆盖
            // 此时应该将菜单显示在消息上方，避免菜单显示在输入框上方
            if (rect.bottom >= inputAreaRect.top) {
                // 将菜单显示在消息上方
                const menuY = rect.top - menuHeight - 4
                // 如果消息上方也没有足够空间，则关闭菜单
                if (menuY < 0) {
                    closeContextMenu()
                    return
                }
                // 检查水平位置，避免与发送按钮重叠
                let menuX = rect.right - menuWidth
                if (sendBtnRect) {
                    // 如果菜单右侧与发送按钮重叠，将菜单向左移动
                    if (menuX + menuWidth > sendBtnRect.left && menuY < sendBtnRect.bottom && menuY + menuHeight > sendBtnRect.top) {
                        menuX = sendBtnRect.left - menuWidth - 8 // 留出一些间距
                        // 如果向左移动后菜单超出左边界，则关闭菜单
                        if (menuX < 0) {
                            closeContextMenu()
                            return
                        }
                    }
                }
                contextMenu.value.y = menuY
                contextMenu.value.x = menuX
                return
            }
            
            // 如果消息没有被输入框覆盖，检查菜单默认位置（消息下方）是否会与输入框重叠
            const menuYDefault = rect.bottom + 4
            const menuBottom = menuYDefault + menuHeight
            // 如果菜单底部会进入输入框区域，则将菜单显示在消息上方
            if (menuBottom >= inputAreaRect.top) {
                const menuY = rect.top - menuHeight - 4
                // 如果消息上方也没有足够空间，则关闭菜单
                if (menuY < 0) {
                    closeContextMenu()
                    return
                }
                // 检查水平位置，避免与发送按钮重叠
                let menuX = rect.right - menuWidth
                if (sendBtnRect) {
                    // 如果菜单右侧与发送按钮重叠，将菜单向左移动
                    if (menuX + menuWidth > sendBtnRect.left && menuY < sendBtnRect.bottom && menuY + menuHeight > sendBtnRect.top) {
                        menuX = sendBtnRect.left - menuWidth - 8 // 留出一些间距
                        // 如果向左移动后菜单超出左边界，则关闭菜单
                        if (menuX < 0) {
                            closeContextMenu()
                            return
                        }
                    }
                }
                contextMenu.value.y = menuY
                contextMenu.value.x = menuX
                return
            }
        }
        
        // 默认情况：菜单显示在消息下方
        let menuX = rect.right - menuWidth
        let menuY = rect.bottom + 4
        
        // 即使不在输入框区域，也检查是否与发送按钮重叠（防止边缘情况）
        if (sendBtnRect) {
            const menuBottom = menuY + menuHeight
            // 检查菜单是否与发送按钮在垂直和水平方向都重叠
            if (menuX + menuWidth > sendBtnRect.left && menuX < sendBtnRect.right && 
                menuBottom > sendBtnRect.top && menuY < sendBtnRect.bottom) {
                // 将菜单向左移动，避免与发送按钮重叠
                menuX = sendBtnRect.left - menuWidth - 8
                // 如果向左移动后菜单超出左边界，尝试显示在消息上方
                if (menuX < 0) {
                    menuY = rect.top - menuHeight - 4
                    menuX = rect.right - menuWidth
                    // 如果消息上方也没有空间，则关闭菜单
                    if (menuY < 0) {
                        closeContextMenu()
                        return
                    }
                }
            }
        }
        
        contextMenu.value.y = menuY
        contextMenu.value.x = menuX
    }

    const handleContextMenu = (e: MouseEvent, msg: ChatMessage) => {
        if (msg.from === username.value && !msg.isRecalled) {
            // 如果已有定时器，先清除
            if (contextMenuTimer.value) {
                clearTimeout(contextMenuTimer.value)
                contextMenuTimer.value = null
            }
            
            const messageEl = e.currentTarget as HTMLElement
            // 优先使用消息气泡容器作为定位元素
            const wrapperEl = messageEl.querySelector('.message-content-wrapper') as HTMLElement || messageEl
            contextMenu.value = {
                visible: true,
                x: 0,
                y: 0,
                targetMsg: msg,
                targetEl: wrapperEl
            }
            nextTick(() => updateContextMenuPosition())
            
            // 设置10秒后自动关闭
            contextMenuTimer.value = setTimeout(() => {
                closeContextMenu()
            }, 10000)
        }
    }

    const triggerRecall = async () => {
        const msg = contextMenu.value.targetMsg
        if (msg && msg.serverMsgId && msg.to) {
            const token = route.query.token as string
            try {
                const res: any = await chatApi.recallMsg({
                    token,
                    msg_id: msg.serverMsgId,
                    to_suid: msg.to
                })
                if (res.message === 'success' || res.info === 'success') {
                    msg.isRecalled = true
                    
                    // 如果是老师模式，需要更新 messageMap 和 chatList 预览
                    if (permissionType.value === 2 && activeChat.value) {
                        // 如果 messageMap 中存在该会话，也需要更新 messageMap 中的消息
                        const targetChatKey = activeChat.value.suid 
                        if (targetChatKey && messageMap.value[targetChatKey]) {
                            // 在 messageMap 中查找并更新对应的消息
                            const list = messageMap.value[targetChatKey]
                            const idx = list.findIndex(
                                m =>
                                    (m.serverMsgId && String(m.serverMsgId) === String(msg.serverMsgId)) ||
                                    (m.id && String(m.id) === String(msg.id))
                            )
                            if (idx !== -1) {
                                list[idx].isRecalled = true
                            }
                            // 同步 messages.value 到 messageMap（确保数据一致）
                            messageMap.value[targetChatKey] = messages.value.slice()
                        }
                        
                        // 消息已经在 messages.value 中，并且已经标记为撤回
                        // 直接使用 messages.value 的最后一条消息来更新预览
                        const last = getLatestForPreview(messages.value)
                        if (last && activeChat.value) {
                            // 直接更新 activeChat（就是 chatList 中的引用）
                            activeChat.value.lastMessage = getPreviewText(last)
                            activeChat.value.time = last.time
                        }
                    } else {
                        // 学生模式，直接更新预览
                        updateChatPreviewOnRecall(msg)
                    }
                    
                    antMessage.success('撤回成功')
                } else {
                    antMessage.error(res.msg || '撤回失败')
                }
            } catch (e) {
                console.error(e)
                antMessage.error('撤回失败')
            }
        }
        closeContextMenu()
    }

    const normalizeTimestamp = (ts?: number | string) => {
        if (!ts && ts !== 0) return undefined
        if (typeof ts === 'string') {
            const num = Number(ts)
            if (!isNaN(num)) {
                return num < 100000000000 ? num * 1000 : num
            }
            const parsed = Date.parse(ts)
            return isNaN(parsed) ? undefined : parsed
        }
        if (typeof ts === 'number') {
            return ts < 100000000000 ? ts * 1000 : ts
        }
        return undefined
    }

    const formatTime = (timestamp: number | string) => {
        if (timestamp === undefined || timestamp === null || timestamp === '') return ''

        let m: any = null

        if (typeof timestamp === 'string') {
            // 先尝试按字符串时间解析（如 2025-12-15 16:40:50）
            m = moment(timestamp)
            if (!m.isValid()) {
                const normalized = normalizeTimestamp(timestamp)
                if (!normalized) return String(timestamp)
                m = moment(normalized)
            }
        } else {
            const normalized = normalizeTimestamp(timestamp)
            if (!normalized) return ''
            m = moment(normalized)
        }

        if (!m || !m.isValid()) return ''

        const now = moment()
        // 如果与当前年份相同，则不展示年份，否则展示年份
        return m.year() === now.year()
            ? m.format('MM-DD HH:mm:ss')
            : m.format('YYYY-MM-DD HH:mm:ss')
    }
    
    const formatListTime = (timestamp?: number) => {
        if(!timestamp) return ''
        const now = moment()
        const msgTime = moment(timestamp)
        const diffDays = now.diff(msgTime, 'days')
        
        if (diffDays === 0) {
            // 今天，显示时间
            return msgTime.format('HH:mm')
        } else if (diffDays === 1) {
            // 昨天
            return '昨天'
        } else if (diffDays < 7) {
            // 一周内，显示星期几（使用中文）
            const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
            return weekdays[msgTime.day()]
        } else {
            // 更早，显示日期
            return msgTime.format('MM-DD')
        }
    }

    const shouldShowTime = (index: number) => {
        if (index === 0) return true
        const currentMsg = messages.value[index]
        const prevMsg = messages.value[index - 1]
        const currTime = normalizeTimestamp(currentMsg.time)
        const prevTime = normalizeTimestamp(prevMsg.time)
        if (!currTime || !prevTime) return true
        return (currTime - prevTime) > 5 * 60 * 1000
    }

    const markActiveChatRead = () => {
      if (!activeChat.value) return
      const chatKey = activeChat.value.suid 
      messages.value.forEach(m => {
        if (m.from === chatKey || m.to === chatKey) {
          m.isRead = true
        }
      })
      activeChat.value.unreadCount = 0
    }

    const handleSend = async (e?: KeyboardEvent) => {
      if (e && !e.shiftKey && e.key === 'Enter') {
          e.preventDefault()
      }
      
      if (!inputText.value.trim()) return
      
      // 检查字数限制
      if (inputText.value.length > 800) {
          antMessage.warning('消息内容不能超过800个字')
          return
      }
      
      if (!targetUser.value) {
          antMessage.warning('No target user found')
          return
      }

      sending.value = true
      const token = route.query.token as string
      const messageContent = inputText.value // 保存消息内容，避免清空后丢失
      try {
          const res: any = await chatApi.getSendMsg({
              token,
              to_suid: targetUser.value,
              content: messageContent,
          })
          if (res.message === 'success' || res.info === 'success') {
              antMessage.success('发送成功')
              
              // 优先使用后端返回的 messages 做展示，避免与 websocket 重复
              const apiMessages = (res.messages || res.data?.messages) as any[] | undefined
              if (Array.isArray(apiMessages) && apiMessages.length > 0) {
                  apiMessages.forEach((m: any, index: number) => {
                      const replyToId = m.reply_to && m.reply_to.id ? m.reply_to.id : undefined
                      const replyToIdStr = replyToId != null ? String(replyToId) : undefined
                      
                      // 如果 websocket 已经基于 data.id 渲染了消息（对应 reply_to.id），这里就不再重复添加
                      if (replyToIdStr) {
                          const duplicated = messages.value.some((ex: ChatMessage) => 
                              (ex.serverMsgId && String(ex.serverMsgId) === replyToIdStr) ||
                              (ex.id && String(ex.id) === replyToIdStr)
                          )
                          if (duplicated) {
                              return
                          }
                      }

                      const sid = m.msg_id ? String(m.msg_id) : (m.id ? String(m.id) : undefined)
                      
                      // 在添加消息之前，先检查该消息 ID 是否已存在（防止与 onMessageSent 重复）
                      if (sid) {
                          // 1. 检查全局去重集合
                          if (seenMessageIds.has(sid)) {
                              return
                          }
                          // 2. 检查当前消息列表
                          const alreadyExists = messages.value.some((ex: ChatMessage) =>
                              (ex.serverMsgId && String(ex.serverMsgId) === sid) ||
                              (ex.id && String(ex.id) === sid)
                          )
                          if (alreadyExists) {
                              seenMessageIds.add(sid)
                              return
                          }
                          // 3. 先记录 ID 到 seenMessageIds，防止 onMessageSent 重复添加
                          seenMessageIds.add(sid)
                      }
                      
                      const newMsg: ChatMessage = {
                        ...currentUserInfo.value,
                        id: sid || String(Date.now()),
                        // getSendMsg 返回的 msg_id 才是真正的消息 ID，用于撤回消息
                        serverMsgId: sid,
                        replyToId,
                        from: username.value,
                        to: targetUser.value,
                        msg: m.content ?? messageContent,
                        time: m.message_time || Date.now(),
                        type: m.type || 'txt',
                        url: m.url || '',
                        isMe: true,
                        isRead: true,
                        avatar: currentUserInfo.value.head_portrait
                      }
                      
                      // 直接添加到 messages（确保立即显示）
                      messages.value.push(newMsg)
                      
                      // 如果是老师模式，同时同步到 messageMap
                      if (permissionType.value === 2 && activeChat.value) {
                        const chatKey = activeChat.value.suid
                        if (chatKey) {
                          if (!messageMap.value[chatKey]) {
                            messageMap.value[chatKey] = []
                          }
                          // 检查 messageMap 中是否已存在该消息
                          const existsInMap = messageMap.value[chatKey].some((ex: ChatMessage) =>
                            (ex.serverMsgId && String(ex.serverMsgId) === sid) ||
                            (ex.id && String(ex.id) === sid)
                          )
                          if (!existsInMap) {
                            messageMap.value[chatKey].push(newMsg)
                          }
                        }
                      }
                  })
              } else {
                  // 兼容老返回结构，保持原来的本地回显逻辑
                  const msgId = res.data?.msg_id ? String(res.data.msg_id) : undefined
                  
                  // 在添加消息之前，先检查该消息 ID 是否已存在（防止与 onMessageSent 重复）
                  if (msgId) {
                      // 1. 检查全局去重集合
                      if (seenMessageIds.has(msgId)) {
                          return
                      }
                      // 2. 检查当前消息列表
                      const alreadyExists = messages.value.some((ex: ChatMessage) =>
                          (ex.serverMsgId && String(ex.serverMsgId) === msgId) ||
                          (ex.id && String(ex.id) === msgId)
                      )
                      if (alreadyExists) {
                          seenMessageIds.add(msgId)
                          return
                      }
                      // 3. 先记录 ID 到 seenMessageIds，防止 onMessageSent 重复添加
                      seenMessageIds.add(msgId)
                  }
                  
                  const newMsg: ChatMessage = {
                    ...currentUserInfo.value,
                    id: msgId || String(Date.now()),
                    serverMsgId: msgId || '',
                    from: username.value,
                    to: targetUser.value,
                    msg: messageContent,
                    time: Date.now(),
                    type: 'txt',
                    isMe: true,
                    isRead: true,
                    avatar: currentUserInfo.value.head_portrait
                  }
                  
                  // 直接添加到 messages（确保立即显示）
                  messages.value.push(newMsg)
                  
                  // 如果是老师模式，同时同步到 messageMap
                  if (permissionType.value === 2 && activeChat.value) {
                    const chatKey = activeChat.value.suid
                    if (chatKey) {
                      if (!messageMap.value[chatKey]) {
                        messageMap.value[chatKey] = []
                      }
                      // 检查 messageMap 中是否已存在该消息
                      const existsInMap = messageMap.value[chatKey].some((ex: ChatMessage) =>
                        (ex.serverMsgId && String(ex.serverMsgId) === msgId) ||
                        (ex.id && String(ex.id) === msgId)
                      )
                      if (!existsInMap) {
                        messageMap.value[chatKey].push(newMsg)
                      }
                    }
                  }
              }

              updateChatListAfterSend('txt', messageContent)
              inputText.value = '' // 在添加消息后再清空输入框
              
              // 强制触发 Vue 的响应式更新
              nextTick(() => {
                scrollToBottom()
              })
              applyLocaleTransform()
          } else {
              // 发送失败，不添加消息，只提示错误
              antMessage.error(res.msg || '发送失败')
          }
      } catch (error) {
          console.error(error)
          antMessage.error('Failed to send message')
      } finally {
          sending.value = false
      }
    }
    
    const handleEmojiClick = (emoji: string) => {
        // 在 Windows 7 上（useEmojiImage 为 true），使用文本格式而不是 emoji 字符，避免输入框显示乱码
        const emojiText = useEmojiImage.value 
            ? (emojiToTextMap[emoji] || emoji)  // Windows 7 使用文本格式
            : emoji  // 正常系统使用 emoji 字符
        
        showEmoji.value = false
        
        // 在光标位置插入 emoji，而不是追加到末尾
        nextTick(() => {
          if (textareaRef.value) {
            // Ant Design Vue 的 textarea 组件，需要获取内部的 textarea 元素
            let textarea: HTMLTextAreaElement | null = null
            
            // 尝试多种方式获取原生 textarea 元素
            if (textareaRef.value.$el) {
              // Vue 3 组件实例
              textarea = textareaRef.value.$el.querySelector('textarea') || textareaRef.value.$el
            } else if (textareaRef.value instanceof HTMLTextAreaElement) {
              // 直接是原生元素
              textarea = textareaRef.value
            } else if (textareaRef.value.resizableTextArea) {
              // Ant Design Vue 的 textarea 组件结构
              textarea = textareaRef.value.resizableTextArea.textArea
            }
            
            if (textarea && typeof textarea.setSelectionRange === 'function') {
              // 获取当前光标位置
              const start = textarea.selectionStart || 0
              const end = textarea.selectionEnd || start
              
              // 在光标位置插入 emoji
              const text = inputText.value
              const newText = text.substring(0, start) + emojiText + text.substring(end)
              inputText.value = newText
              
              // 设置光标位置到插入后的位置
              const newCursorPos = start + emojiText.length
              textarea.focus()
              setTimeout(() => {
                if (textarea) {
                  textarea.setSelectionRange(newCursorPos, newCursorPos)
                }
              }, 0)
            } else {
              // 如果无法获取光标位置，则追加到末尾（兼容处理）
              inputText.value += emojiText
              if (textareaRef.value && typeof textareaRef.value.focus === 'function') {
                textareaRef.value.focus()
              }
            }
          }
        })
    }
    
    const handleImageUpload = () => {
        fileInput.value?.click()
    }
    
    const onFileSelected = async (e: Event) => {
        const files = (e.target as HTMLInputElement).files
        if (files && files.length > 0) {
             const file = files[0]
             try {
                 sending.value = true
                 // 将文件读取为二进制数据，然后转换为 Blob 并添加到 FormData
                 const fileData = await new Promise<ArrayBuffer>((resolve, reject) => {
                     const reader = new FileReader()
                     reader.onload = () => resolve(reader.result as ArrayBuffer)
                     reader.onerror = reject
                     reader.readAsArrayBuffer(file)
                 })
                 // 将 ArrayBuffer 转换为 Blob，然后添加到 FormData（二进制格式）
                 const blob = new Blob([fileData], { type: file.type || 'image/png' })
                 const formData = new FormData()
                 formData.append('fname', blob, file.name)
                 const res: any = await chatApi.getUpload(formData)
                 if (res && res.code === 1 && res.message === '上传成功') {
                     const url = res.result
                     const token = route.query.token as string
                     const sendRes: any = await chatApi.getSendMsg({
                         token,
                         to_suid: targetUser.value,
                         images: url,
                         type: 'img'
                     })
                     
                     if (sendRes.message === 'success' || sendRes.info === 'success') {
                        antMessage.success('发送成功')

                        const apiMessages = (sendRes.messages || sendRes.data?.messages) as any[] | undefined
                        if (Array.isArray(apiMessages) && apiMessages.length > 0) {
                            apiMessages.forEach((m: any) => {
                                const replyToId = m.reply_to && m.reply_to.id ? m.reply_to.id : undefined
                                const replyToIdStr = replyToId != null ? String(replyToId) : undefined
                                
                                if (replyToIdStr) {
                                    const duplicated = messages.value.some((ex: ChatMessage) => 
                                        (ex.serverMsgId && String(ex.serverMsgId) === replyToIdStr) ||
                                        (ex.id && String(ex.id) === replyToIdStr)
                                    )
                                    if (duplicated) return
                                }

                                const sid = m.msg_id ? String(m.msg_id) : (m.id ? String(m.id) : undefined)
                                
                                // 在添加消息之前，先检查该消息 ID 是否已存在（防止与 onMessageSent 重复）
                                if (sid) {
                                    // 1. 检查全局去重集合
                                    if (seenMessageIds.has(sid)) {
                                        return
                                    }
                                    // 2. 检查当前消息列表
                                    const alreadyExists = messages.value.some((ex: ChatMessage) =>
                                        (ex.serverMsgId && String(ex.serverMsgId) === sid) ||
                                        (ex.id && String(ex.id) === sid)
                                    )
                                    if (alreadyExists) {
                                        seenMessageIds.add(sid)
                                        return
                                    }
                                }
                                
                                const newMsg: ChatMessage = {
                                    ...currentUserInfo.value,
                                    id: sid || String(Date.now()),
                                    // getSendMsg 返回的 msg_id 才是真正的消息 ID，用于撤回消息
                                    serverMsgId: sid,
                                    replyToId,
                                    from: username.value,
                                    to: targetUser.value,
                                    msg: '',
                                    url: m.url || url,
                                    time: m.message_time || Date.now(),
                                    type: m.type || 'img',
                                    isMe: true,
                                    isRead: true,
                                    avatar: currentUserInfo.value.head_portrait
                                }
                                
                                // 直接添加到 messages（确保立即显示）
                                messages.value.push(newMsg)
                                
                                // 如果是老师模式，同时同步到 messageMap
                                if (permissionType.value === 2 && activeChat.value) {
                                    const chatKey = activeChat.value.suid
                                    if (chatKey) {
                                        if (!messageMap.value[chatKey]) {
                                            messageMap.value[chatKey] = []
                                        }
                                        // 检查 messageMap 中是否已存在该消息
                                        const existsInMap = messageMap.value[chatKey].some((ex: ChatMessage) =>
                                            (ex.serverMsgId && String(ex.serverMsgId) === sid) ||
                                            (ex.id && String(ex.id) === sid)
                                        )
                                        if (!existsInMap) {
                                            messageMap.value[chatKey].push(newMsg)
                                        }
                                    }
                                }
                                
                                if (sid) seenMessageIds.add(sid)
                            })
                        } else {
                            // 兼容老返回结构，尝试从 sendRes.data 获取 msg_id
                            const msgId = sendRes.data?.msg_id ? String(sendRes.data.msg_id) : undefined
                            
                            // 在添加消息之前，先检查该消息 ID 是否已存在（防止与 onMessageSent 重复）
                            if (msgId) {
                                // 1. 检查全局去重集合
                                if (seenMessageIds.has(msgId)) {
                                    // 消息已存在，跳过添加
                                    return
                                }
                                // 2. 检查当前消息列表
                                const alreadyExists = messages.value.some((ex: ChatMessage) =>
                                    (ex.serverMsgId && String(ex.serverMsgId) === msgId) ||
                                    (ex.id && String(ex.id) === msgId)
                                )
                                if (alreadyExists) {
                                    seenMessageIds.add(msgId)
                                    return
                                }
                            }
                            
                            const newMsg: ChatMessage = {
                                ...currentUserInfo.value,
                                id: msgId || String(Date.now()),
                                // getSendMsg 返回的 msg_id 才是真正的消息 ID，用于撤回消息
                                serverMsgId: msgId,
                                from: username.value,
                                to: targetUser.value,
                                msg: '',
                                url: url,
                                time: Date.now(),
                                type: 'img',
                                isMe: true,
                                isRead: true,
                                avatar: currentUserInfo.value.head_portrait
                            }
                            
                            // 直接添加到 messages（确保立即显示）
                            messages.value.push(newMsg)
                            
                            // 如果是老师模式，同时同步到 messageMap
                            if (permissionType.value === 2 && activeChat.value) {
                                const chatKey = activeChat.value.suid
                                if (chatKey) {
                                    if (!messageMap.value[chatKey]) {
                                        messageMap.value[chatKey] = []
                                    }
                                    // 检查 messageMap 中是否已存在该消息
                                    const existsInMap = messageMap.value[chatKey].some((ex: ChatMessage) =>
                                        (ex.serverMsgId && String(ex.serverMsgId) === msgId) ||
                                        (ex.id && String(ex.id) === msgId)
                                    )
                                    if (!existsInMap) {
                                        messageMap.value[chatKey].push(newMsg)
                                    }
                                }
                            }
                            
                            if (msgId) seenMessageIds.add(msgId)
                        }

                        updateChatListAfterSend('img', '')
                        scrollToBottom()
                        applyLocaleTransform()
                     } else {
                         // 发送失败，不添加消息，只提示错误
                         antMessage.error(sendRes.msg || '发送失败')
                     }
                 } else {
                     antMessage.error(res.msg || 'Upload failed')
                 }
             } catch(err) {
                 console.error(err)
                 antMessage.error('Upload failed')
             } finally {
                 sending.value = false
                 if (fileInput.value) fileInput.value.value = ''
             }
        }
    }

    const scrollToBottom = () => {
        nextTick(() => {
            if (messageListRef.value) {
                messageListRef.value.scrollTop = messageListRef.value.scrollHeight
            }
        })
    }

    // 窗口大小变化时，直接滚动到底部
    const handleWindowResize = () => {
        // 使用 requestAnimationFrame 和 nextTick 确保在 DOM 更新后执行
        requestAnimationFrame(() => {
            nextTick(() => {
                if (messageListRef.value) {
                    messageListRef.value.scrollTop = messageListRef.value.scrollHeight
                }
            })
        })
    }

    const handleScroll = (e: Event) => {
        const target = e.target as HTMLElement
        // 当滚动条接近顶部时（例如距离顶部小于等于 50px），自动加载更多历史消息
        if (target.scrollTop <= 50 && !isLoadingHistory.value && hasMoreHistory.value) {
            handleLoadMore()
        }
        // 滚动时更新右键菜单位置，避免菜单与输入框重叠
        if (contextMenu.value.visible) {
            updateContextMenuPosition()
        }
    }

    const handleLoadMore = async () => {
        if (isLoadingHistory.value || !hasMoreHistory.value) return
        const token = route.query.token as string
        if (!token || messages.value.length === 0) return

        // 取当前第一条消息的 id 作为 last_msg_id
        const first = messages.value[0]
        const lastId = first.serverMsgId || first.id
        if (!lastId) return

        // 根据当前模式确定对端 suid
        let to_suid: string | number = ''
        if (permissionType.value === 2) {
            // 老师模式：使用当前会话的 suid
            to_suid = activeChat.value?.suid || ''
        } else {
            // 学生模式：使用 targetUser（老师 suid）
            to_suid = targetUser.value
        }
        if (!to_suid) return

        await fetchHistory(token, to_suid, lastId, true)
    }

    const fetchChatInfo = async () => {
        const acceptLang = getParameterValue('Accept-Language') || (route.query['Accept-Language'] as string) || ''
        languageTraditional.value = acceptLang === 'zh-Hant'
        const token = route.query.token as string
        if (!token) {
            antMessage.error('Missing token')
            initLoading.value = false
            return
        }

        try {
            const res: any = await chatApi.getChatInfo({ token, type:0 })
            const data = res.data || res 
            if (data) {
                username.value = data.easemob_username
                password.value = data.easemob_pwd 
                permissionType.value = parseInt(data.permission_type)
                
                loginIMWithPassword(username.value, password.value)
                isConnected.value = true

                if (permissionType.value === 2) {
                    activeChat.value = null // Ensure no chat is selected by default for teacher
                    fetchChatList(token)
                } else {
                    // User mode (permissionType 1): use teacher's suid from response
                    const teacherSuid = data.teacher ? data.teacher.teacher_suid : ''
                    // 无论当前是否有历史消息，都先把 targetUser 设为老师的 suid，避免为空导致后续发送失败
                    if (teacherSuid) {
                        targetUser.value = String(teacherSuid)
                    }
                    fetchHistory(token, teacherSuid)
                }
            }
        } catch (error) {
            console.error('Failed to get chat info', error)
            antMessage.error('Failed to load chat info')
        } finally {
            initLoading.value = false
        }
    }
    
    const selectChat = (user: ChatUser) => {
        // 先去重该会话的所有重复条目，保留当前点击的，未读清零
        dedupeChatListFor(user, true)

        // 从去重后的 chatList 中找到对应的项，确保 activeChat 指向 chatList 中的对象
        const chatInList = chatList.value.find(c => 
            (c.suid && user.suid && c.suid === user.suid) ||
            (c.easemob_name && user.easemob_name && c.easemob_name === user.easemob_name) ||
            (c.username && user.username && c.username === user.username)
        )
        
        // 使用 chatList 中的对象作为 activeChat，而不是传入的 user
        activeChat.value = chatInList || user

        // 当点击当前 user-list-item 时，如果该会话在列表里的“最新一条”是撤回消息，
        // 则把预览改成这条会话中“最新一条未撤回”的消息
        // if (permissionType.value === 2) {
        //   const chatKey = getChatKeyFromUser(user)
        //   const list = chatKey ? messageMap.value[chatKey] : undefined
        //   if (list && list.length) {
        //     const last = list[list.length - 1]
        //     if (last && last.isRecalled) {
        //       // 从后往前找到第一条未撤回的消息
        //       let latestNonRecalled: ChatMessage | null = null
        //       for (let i = list.length - 2; i >= 0; i--) {
        //         if (!list[i].isRecalled) {
        //           latestNonRecalled = list[i]
        //           break
        //         }
        //       }
        //       if (latestNonRecalled) {
        //         user.lastMessage = getPreviewText(latestNonRecalled)
        //         user.time = latestNonRecalled.time
        //       }
        //     }
        //   }
        // }

        targetUser.value = user.suid
        user.historyLoaded = true
        messages.value = []
        hasMoreHistory.value = true
        // 清除该用户的未读消息数（去重后已经清零，这里再确保一次）
        if (user.unreadCount && user.unreadCount > 0) {
            user.unreadCount = 0
        }
        const token = route.query.token as string
        fetchHistory(token, user.suid, user.last_msg_id)
        nextTick(() => {
          markActiveChatRead()
        })
    }

    const fetchChatList = async (token: string) => {
        try {
            const res: any = await chatApi.getChats({ token })
            const list = res.data || res || []
            if (Array.isArray(list)) {
                chatList.value = list.map((item: any) => {
                    // getChats 接口返回的 last_msg_time 直接使用，不需要转换
                    return {
                        username: item.username || item.user_id || item.uid || item.easemob_username,
                        nickname: item.nickname || item.user_name || item.name,
                        avatar: item.head_portrait,
                        lastMessage: item.last_msg || item.content || item.msg,
                        time: item.last_msg_time, // 直接使用 last_msg_time，不需要转换
                        // 首次/刷新时用后端返回的新未读数；后续实时由 ws 推送累加
                        unreadCount: item.new_msg_cnt || item.unread_count || item.unread || 0,
                        suid: item.suid || item.user_id, // Ensure suid is mapped if available
                        last_msg_id: item.last_msg_id || '',
                        easemob_name: item.easemob_username || item.easemob_name || item.username, // 存储 easemob 用户名，用于匹配消息的 to 字段
                        historyLoaded: false
                    }
                })
            }
            if (permissionType.value === 2 && !activeChat.value && chatList.value.length > 0) {
                selectChat(chatList.value[0]) // teacher defaults to first user
            }
        } catch (e) {
            console.error('Failed to get chat list', e)
        }
    }
    const fetchHistory = async (
        token: string,
        to_suid: number | string,
        last_msg_id: number | string = '',
        append: boolean = false
    ) => {
        try {
            isLoadingHistory.value = true
            
            // 先调用 setOnline 接口
            await chatApi.setOnline({ token, online: 1 })
            
            const params: any = { token, to_suid, last_msg_id }
            const res: any = await chatApi.getChatMessage(params)
            let list = res.data.messages || []
            list = list.reverse()
            // 如果本次返回的历史消息少于 100 条，说明已经到底了
            hasMoreHistory.value = Array.isArray(res.data.messages) && res.data.messages.length >= 100
            const userData = res.data.user || {}
            const toUserData = res.data.to_user || {}
            
            // Save current user info for later use
            currentUserInfo.value = userData
            currentChatUserInfo.value = toUserData
            
            const adaptedMessages = list.map((m: any) => {
                let type = 'txt'
                let url = ''
                
                if (m.images && (Array.isArray(m.images) ? m.images.length > 0 : m.images)) {
                    type = 'img'
                    url = Array.isArray(m.images) ? m.images[0] : m.images
                } else if (m.video && m.video.url) {
                    type = 'video'
                    url = m.video.url
                } else if (m.audio && m.audio.url) {
                    type = 'audio'
                    url = m.audio.url
                } else if (m.pdf && m.pdf.url) {
                    type = 'pdf'
                    url = m.pdf.url
                }

                const isMe = m.is_me == 1 || m.is_me === true
                const otherUser = String(to_suid)
                const additionalInfo = isMe ? userData : toUserData
                
                const adapted = {
                    ...additionalInfo,
                    id: String(m.id),
                    serverMsgId: String(m.id),
                    from: isMe ? username.value : otherUser,
                    to: isMe ? otherUser : username.value,
                    msg: m.content || '',
                    time: m.message_time || Date.now(), // getChatMessage 接口返回的 message_time 直接使用，不需要转换
                    isRecalled: false,
                    type: type,
                    url: url,
                    isMe: isMe,
                    // 兼容历史消息中可能包含的 reply_to 结构
                    replyToId: m.reply_to && m.reply_to.id ? m.reply_to.id : undefined,
                    avatar: additionalInfo.head_portrait
                }
                return adapted
            })
            let displayedMessages = adaptedMessages
            
            // 学生模式下，保证 targetUser 始终有值：
            // 1）优先使用已有的 targetUser；
            // 2）如果当前没有且有历史消息，用历史消息推断；
            // 3）如果连历史消息都没有，则兜底用 to_suid（老师 suid）。
            if (permissionType.value !== 2) {
                if (!targetUser.value) {
                    if (adaptedMessages.length > 0) {
                        const other = adaptedMessages.find((m: any) => !m.isMe)
                        if (other) {
                            targetUser.value = other.from
                        } else {
                            targetUser.value = adaptedMessages[0].to
                        }
                    } else if (to_suid) {
                        targetUser.value = String(to_suid)
                    }
                }
            }

            console.log('打印数据====',displayedMessages)

            if (append) {
                // 追加到顶部（加载更多历史）
                const messageListEl = messageListRef.value
                const oldScrollHeight = messageListEl ? messageListEl.scrollHeight : 0

                // 去重：避免重复消息
                const existingIds = new Set(messages.value.map(m => m.id))
                const newMessages = displayedMessages.filter(m => !existingIds.has(m.id))

                if (newMessages.length > 0) {
                    // 将新消息的 ID 记录到全局去重集合
                    newMessages.forEach(m => {
                        if (m.serverMsgId) seenMessageIds.add(String(m.serverMsgId))
                        if (m.id) seenMessageIds.add(String(m.id))
                    })
                    messages.value = newMessages.concat(messages.value)
                    nextTick(() => {
                        if (messageListEl) {
                            const newScrollHeight = messageListEl.scrollHeight
                            messageListEl.scrollTop = newScrollHeight - oldScrollHeight
                        }
                    })
                }
            } else {
                // 首次加载或切换会话
                // 这里要考虑一种竞争情况：在 getChatMessage 请求期间，WS 可能已经推送了最新一条消息。
                // 为了避免“HTTP + WS”各插入一遍造成重复，同时又不能把 WS 里已经有的消息丢掉，
                // 我们在这里做一次基于 ID 的合并。
                if (permissionType.value === 2) {
                  // 老师模式：按会话维度合并到 messageMap
                  const chatKey = getChatKeyFromHistory(toUserData, to_suid)
                  const prevList =
                    chatKey && messageMap.value[chatKey] ? messageMap.value[chatKey] : []
                  const existingIds = new Set(
                    prevList
                      .map(m => m.serverMsgId || m.id)
                      .filter(Boolean)
                      .map(id => String(id))
                  )
                  const newMessages = displayedMessages.filter(m => {
                    const id = m.serverMsgId || m.id
                    if (!id) return true
                    const idStr = String(id)
                    if (existingIds.has(idStr)) return false
                    existingIds.add(idStr)
                    return true
                  })
                  // 历史接口返回的一般是更早的消息，因此放在前面；已有列表(可能来自 WS)放在后面
                  const merged = newMessages.concat(prevList)
                  if (chatKey) {
                    messageMap.value[chatKey] = merged
                    // 同步列表预览（使用“真正的最后一条消息”，包括撤回）
                    syncChatPreviewFromList(chatKey, merged)
                    // 历史加载完成，清理该会话的 WS 待处理缓存，避免后续重复判断未读
                    if (wsPendingMap.value[chatKey]) {
                      delete wsPendingMap.value[chatKey]
                    }
                  }
                  // 当前选中的会话直接展示合并后的列表
                  messages.value = merged.slice()
                  // 将所有消息的 ID 记录到全局去重集合，供后续 WS 去重使用
                  merged.forEach(m => {
                    if (m.serverMsgId) seenMessageIds.add(String(m.serverMsgId))
                    if (m.id) seenMessageIds.add(String(m.id))
                  })
                } else {
                  // 学生模式：只有一个会话，直接和当前 messages 合并
                  const prevList = messages.value.slice()
                  const existingIds = new Set(
                    prevList
                      .map(m => m.serverMsgId || m.id)
                      .filter(Boolean)
                      .map(id => String(id))
                  )
                  const newMessages = displayedMessages.filter(m => {
                    const id = m.serverMsgId || m.id
                    if (!id) return true
                    const idStr = String(id)
                    if (existingIds.has(idStr)) return false
                    existingIds.add(idStr)
                    return true
                  })
                  const merged = newMessages.concat(prevList)
                  messages.value = merged
                  merged.forEach(m => {
                    if (m.serverMsgId) seenMessageIds.add(String(m.serverMsgId))
                    if (m.id) seenMessageIds.add(String(m.id))
                  })
                }
                scrollToBottom()
            }
            applyLocaleTransform()
            // 老师模式下，标记对应会话已加载过历史，避免未读未选中时的 ws 重复
            if (permissionType.value === 2) {
                const chat = chatList.value.find(
                    c => c.suid == to_suid || c.username == to_suid || c.easemob_name == to_suid
                )
                if (chat) chat.historyLoaded = true
            }
        } catch (e) {
            console.error('Failed to get history', e)
        } finally {
            isLoadingHistory.value = false
        }
    }

    // 获取 WebSocket 消息的头像
    // 优先级：currentChatUserInfo.head_portrait > msg.ext.from.head_portrait > chatList 中的 head_portrait
    const getAvatarFromWsMessage = (msg: any, sender: string): string => {
      // 1. 优先使用 currentChatUserInfo 中的头像
      if (currentChatUserInfo.value.head_portrait) {
        return currentChatUserInfo.value.head_portrait
      }
      
      // 2. 如果 currentChatUserInfo 没有头像，尝试使用 ws 返回的 ext.from.head_portrait
      if (msg.ext?.from?.head_portrait) {
        return msg.ext.from.head_portrait
      }
      
      // 3. 如果还没有，从 chatList 中查找对应的 head_portrait（存储在 avatar 字段中）
      const chatItem = chatList.value.find(
        c => c.easemob_name === sender || String(c.suid) === sender || c.username === sender
      )
      if (chatItem?.avatar) {
        return chatItem.avatar
      }
      
      // 4. 如果都没有，返回空字符串
      return ''
    }

    // Event Listeners
    const onMessageReceived = (e: any) => {
      const msg = e.detail
      console.log('打印收到的msg====',msg)

      // 如果是自己（当前登录帐号）发出的消息，已经通过 HTTP getSendMsg 在本地回显过，
      // 这里的普通消息推送再次到达时直接忽略，避免 HTTP + WS 各回显一条导致重复。
      if (msg.from && msg.from === username.value) {
        return
      }
      const body = msg.bodies ? msg.bodies[0] : {}
      let type = body.type || 'txt'
      
      // 按照优先级获取消息内容：e.detail.data > e.detail.data.ext.imgs > [Message]
      let content = '[Message]'
      let url = body.url || ''
      
      if (msg.data) {
        // 如果 data 是字符串或数字，直接使用
        if (typeof msg.data === 'string' || typeof msg.data === 'number') {
          content = String(msg.data)
        } else if (msg.ext?.imgs) {
          // 如果 data 是对象，尝试获取 ext.imgs（图片消息）
          type = 'img'
          url = msg.ext.imgs
          content = '' // 图片消息不需要文本内容
        } else {
          // 如果 data 存在但没有 ext.imgs，使用 data 本身（转换为字符串）
          content = String(msg.data)
        }
      } else if (msg.ext?.imgs) {
        // 如果 data 没值，尝试获取 ext.imgs（图片消息）
        type = 'img'
        url = msg.ext.imgs
        content = '' // 图片消息不需要文本内容
      }

      // 环信 SDK 撤回类型消息处理：ext.msg_id 表示被撤回的原消息 ID
      // 收到该类消息时，根据 msg_id 找到对应消息，标记为撤回，不再新增一条消息
      const recallMsgId =
        msg.ext?.msg_id ||
        msg.ext?.msgId ||
        (msg.ext?.recalledMessage && msg.ext.recalledMessage.msg_id)

      if (recallMsgId) {
        const targetId = String(recallMsgId)
        const idx = messages.value.findIndex(
          (m: ChatMessage) =>
            (m.serverMsgId && String(m.serverMsgId) === targetId) ||
            (m.id && String(m.id) === targetId)
        )
        if (idx !== -1) {
          messages.value[idx].isRecalled = true
          updateChatPreviewOnRecall(messages.value[idx])
        }
        // 不再继续按普通消息流程处理，直接返回
        return
      }

      // 检查 msg.ext.id 是否与 getChatMessage 返回的消息 id 重复
      let wsExtId: string | undefined
      if (msg.ext && msg.ext.id) {
        wsExtId = String(msg.ext.id)
      }
      
      // 检查消息 ID 是否已存在（与 getChatMessage 返回的 id 或 serverMsgId 比较）
      let isExistingMessage = false
      if (wsExtId) {
        // 1. 全局去重集合检查
        if (seenMessageIds.has(wsExtId)) {
          isExistingMessage = true
        }
        // 2. 仅在老师模式下，在对应会话的 messageMap 中查重（不跨会话）
        const sender = msg.from
        if (!isExistingMessage && permissionType.value === 2 && sender) {
          const targetChat = chatList.value.find(
            c => c.easemob_name === sender || String(c.suid) === sender || c.username === sender
          )
          const chatKey = targetChat
            ? getChatKeyFromUser(targetChat)
            : normalizeChatKey(sender, undefined, sender)
          if (chatKey && messageMap.value[chatKey]) {
            const found = messageMap.value[chatKey].some(
              (m: ChatMessage) =>
                (m.id && String(m.id) === wsExtId) ||
                (m.serverMsgId && String(m.serverMsgId) === wsExtId)
            )
            if (found) isExistingMessage = true
          }
        }
        // 3. 学生模式下，如果发送者就是当前会话，才在 messages 中查重
        if (!isExistingMessage && permissionType.value !== 2) {
          if (targetUser.value === msg.from) {
            isExistingMessage = messages.value.some(
              (m: ChatMessage) =>
                (m.id && String(m.id) === wsExtId) ||
                (m.serverMsgId && String(m.serverMsgId) === wsExtId)
            )
          }
        }
      }
      
      // If Teacher Mode
      if (permissionType.value === 2) {
          const sender = msg.from // 发送者（用户）的 easemob_name
          const receiver = msg.to // 接收者（老师）的 easemob_name
          
          // websocket 返回的 time 字段需要转换（从秒转毫秒）
          // 先将字符串转换为数字，然后判断是否需要从秒转毫秒
          let msgTime = Date.now()
          if (msg.time) {
              const timeNum = typeof msg.time === 'string' ? parseInt(msg.time, 10) : msg.time
              msgTime = timeNum > 1000000000000 ? timeNum : timeNum * 1000
          }
          
          // Update Chat List - 使用 msg.from 与 chatList 中的 easemob_name 字段进行比较
          const existingChat = chatList.value.find(c => c.easemob_name === sender)
          
          if (existingChat) {
              const chatKey = getChatKeyFromWs(msg) || sender

              // 如果该会话历史还未拉取（只拿了 getChats，historyLoaded=false），
              // 先缓存消息，更新预览，未读数按「getChats 未读基数 + 额外新 WS 条数」来计算，
              // 避免把首屏离线消息重复统计，又能保证进入页面后真正新增的消息会继续累加
              if (!existingChat.historyLoaded) {
                  const pendingMsg: ChatMessage = {
                    ...currentChatUserInfo.value,
                    id: wsExtId || msg.id,
                    serverMsgId: wsExtId || msg.id,
                    from: msg.from,
                    to: msg.to,
                    msg: content,
                    time: msgTime,
                    type: type,
                    url: url,
                    isMe: false,
                    isRead: false,
                    avatar: getAvatarFromWsMessage(msg, sender)
                  }

                  if (chatKey) {
                    if (!messageMap.value[chatKey]) messageMap.value[chatKey] = []
                    messageMap.value[chatKey].push(pendingMsg)
                    if (!wsPendingMap.value[chatKey]) wsPendingMap.value[chatKey] = []
                    wsPendingMap.value[chatKey].push(pendingMsg)
                    syncChatPreviewFromList(chatKey, messageMap.value[chatKey])
                  }

                  // 计算这条消息是否属于「超出后端已统计的未读范围」
                  // backendUnread = getChats 返回的未读基数（existingChat.unreadCount）
                  // pendingCount = 当前我们已接收到的该会话 WS 条数
                  const backendUnread = existingChat.unreadCount || 0
                  const pendingCount =
                    chatKey && wsPendingMap.value[chatKey]
                      ? wsPendingMap.value[chatKey].length
                      : 0
                  // 当 pendingCount > backendUnread 时，说明这条以及之后的 WS 是「进入页面后新增」的，才需要继续 +1
                  if (pendingCount > backendUnread) {
                    existingChat.unreadCount = backendUnread + (pendingCount - backendUnread)
                  }

                  if (wsExtId) seenMessageIds.add(wsExtId)
                  if (msg.id) seenMessageIds.add(String(msg.id))

                                    return
              }

              // 历史已加载的正常流程
              existingChat.lastMessage = type === 'img' ? '[图片]' : content
              existingChat.time = msgTime
              
              if (activeChat.value?.easemob_name === sender) {
                  existingChat.unreadCount = 0
              } else {
                  if (!isExistingMessage) {
                      existingChat.unreadCount = (existingChat.unreadCount || 0) + 1
                  }
              }
              
                        } else {
              // 新用户消息，直接在列表中创建新项，而不是调用 fetchChatList
              // 如果当前没有选中该对话框（或没有选中任何对话框），且消息不是已存在的，设置未读数为1
              const isNotActiveChat = !activeChat.value || activeChat.value.easemob_name !== sender
              const newChat: ChatUser = {
                  username: sender,
                  nickname: sender, // 如果没有其他信息，使用 username 作为 nickname
                  avatar: '', // 可以从消息中获取或后续更新
                  lastMessage: type === 'img' ? '[图片]' : content,
                  time: msgTime,
                  // 如果消息 ID 已存在，不设置未读数；如果不存在且当前窗口不是发送者，设置未读数为1
                  unreadCount: (isNotActiveChat && !isExistingMessage) ? 1 : 0,
                  suid: sender, // 暂时使用 username，后续可以从 API 获取正确的 suid
                  last_msg_id: msg.id || '',
                  easemob_name: sender, // 存储发送者的 easemob_name（msg.from），用于后续匹配
                  historyLoaded: false
              }
              // 添加到列表顶部
              chatList.value.unshift(newChat)
          }
          
          // 如果消息 ID 已存在，不添加消息，直接返回
          if (isExistingMessage) {
              console.log('消息已存在，跳过添加:', wsExtId)
              return
          }
          
          // 写入多会话消息池（教师模式）
          // 注意：这里的会话 key 要与历史记录的 key（suid）保持一致，优先使用 existingChat.suid
          const primaryKey = existingChat && existingChat.suid
            ? String(existingChat.suid)
            : getChatKeyFromWs(msg) || sender
          const resolvedKey = primaryKey
          if (resolvedKey) {
            if (!messageMap.value[resolvedKey]) {
              messageMap.value[resolvedKey] = []
            }
            const newMsg: ChatMessage = {
              ...currentChatUserInfo.value,
              id: wsExtId || msg.id, // 优先使用 ext.id，如果没有则使用 msg.id
              serverMsgId: wsExtId || msg.id,
              from: msg.from,
              to: msg.to,
              msg: content,
              time: msgTime,
              type: type,
              url: url,
              isMe: false,
              isRead: activeChat.value?.easemob_name === sender || activeChat.value?.suid === sender,
              avatar: getAvatarFromWsMessage(msg, sender)
            }
            messageMap.value[resolvedKey].push(newMsg)
            // 无论当前是否在该会话，都刷新该会话的预览
            syncChatPreviewFromList(resolvedKey, messageMap.value[resolvedKey])

            // 当前正在查看该会话时，同步到 messages：
            // 使用 suid（resolvedKey）匹配当前激活会话，保证不同学生的消息不会都堆到同一个聊天框
            const isCurrent =
              (activeChat.value && String(activeChat.value.suid || '') === resolvedKey) ||
              activeChat.value?.easemob_name === sender
            if (isCurrent) {
              messages.value = messageMap.value[resolvedKey]
              if (wsExtId) seenMessageIds.add(wsExtId)
              if (msg.id) seenMessageIds.add(String(msg.id))
              scrollToBottom()
              applyLocaleTransform()
            } else {
              if (wsExtId) seenMessageIds.add(wsExtId)
              if (msg.id) seenMessageIds.add(String(msg.id))
            }
          }
          
      } else {
          // Student Mode
          if (!targetUser.value) targetUser.value = msg.from
          
          // websocket 返回的 time 字段需要转换（从秒转毫秒）
          // 先将字符串转换为数字，然后判断是否需要从秒转毫秒
          let msgTime = Date.now()
          if (msg.time) {
              const timeNum = typeof msg.time === 'string' ? parseInt(msg.time, 10) : msg.time
              msgTime = timeNum > 1000000000000 ? timeNum : timeNum * 1000
          }
          
          // 学生模式同样做去重逻辑
          // 如果消息 ID 已存在，不添加消息，直接返回
          if (isExistingMessage) {
              console.log('消息已存在，跳过添加:', wsExtId)
              return
          }

          messages.value.push({
            ...currentChatUserInfo.value,
            id: wsExtId || msg.id, // 优先使用 ext.id，如果没有则使用 msg.id
            serverMsgId: wsExtId || msg.id,
            from: msg.from,
            to: msg.to,
            msg: content,
            time: msgTime,
            type: type,
            url: url,
            isMe: false,
            isRead: targetUser.value === msg.from,
            avatar: getAvatarFromWsMessage(msg, msg.from)
          })
          // 记录消息 ID，防止后续重复
          if (wsExtId) seenMessageIds.add(wsExtId)
          if (msg.id) seenMessageIds.add(String(msg.id))
          scrollToBottom()
          applyLocaleTransform()
      }
    }

    const onMessageSent = (e: any) => {
      const msg = e.detail
      console.log('[onMessageSent] receive ws sent event', msg)
      console.log('[onMessageSent] current messages.value.length:', messages.value.length)

      // 由于自己发送的消息已经通过 HTTP getSendMsg 在本地回显，这里直接忽略自己发送的 WS 回调，避免重复
      if (msg.from && msg.from === username.value) {
        console.log('[onMessageSent] ignore self sent message (already rendered via HTTP)', {
          from: msg.from,
          username: username.value
        })
        return
      }

      // 老师模式下：确保只处理当前选中的会话，避免"有多少用户就重复多少次"
      if (permissionType.value === 2 && activeChat.value) {
        const activeSuid = activeChat.value.suid ? String(activeChat.value.suid) : ''
        const activeEasemob = activeChat.value.easemob_name || activeChat.value.username || ''
        const msgTo = msg.to ? String(msg.to) : ''
        // 如果 ws 推送的目标并不是当前会话，直接跳过
        if (
          msgTo &&
          activeSuid &&
          msgTo !== activeSuid &&
          msgTo !== activeEasemob
        ) {
          console.log('[onMessageSent] skip because not current active chat', {
            msgTo,
            activeSuid,
            activeEasemob
          })
          return
        }
      }

      // 统一获取本条消息的唯一 ID（用于去重）
      const sentId = msg.serverMsgId || msg.id || msg.ext?.id
      if (sentId) {
        const idStr = String(sentId)
        // 1）全局去重集合里已经存在，说明通过 HTTP getSendMsg 已经渲染过，直接跳过
        if (seenMessageIds.has(idStr)) {
          console.log('[onMessageSent] skip because id already in seenMessageIds', { idStr })
          return
        }
        // 2）当前消息列表里已经有相同 id/serverMsgId 的消息，也视为重复
        const duplicated = messages.value.some(m =>
          (m.serverMsgId && String(m.serverMsgId) === idStr) ||
          (m.id && String(m.id) === idStr)
        )
        if (duplicated) {
          seenMessageIds.add(idStr)
          console.log('[onMessageSent] skip because id already in messages', { idStr })
          return
        }
      }

      // 3）额外检查：如果消息内容、发送者、接收者都相同，且时间相近（5秒内），也视为重复
      // 这是为了防止 ID 不一致但实际是同一消息的情况
      const msgContent = msg.msg || msg.data || ''
      const msgFrom = msg.from || ''
      const msgTo = msg.to ? String(msg.to) : ''
      const recentDuplicate = messages.value.some(m => {
        if (m.isMe && m.from === msgFrom && m.to === msgTo) {
          const contentMatch = (m.msg === msgContent || m.msg === String(msg.data))
          const timeDiff = Math.abs((m.time || 0) - (msg.time || Date.now()))
          // 如果内容相同且时间差在5秒内，认为是同一条消息
          if (contentMatch && timeDiff < 5000) {
            return true
          }
        }
        return false
      })
      if (recentDuplicate) {
        // 如果找到重复，也记录 ID（如果有的话）
        if (sentId) {
          seenMessageIds.add(String(sentId))
        }
        console.log('[onMessageSent] skip because found recent duplicate by content/time', {
          msgContent,
          msgFrom,
          msgTo
        })
        return
      }

      // websocket 返回的 time 字段需要转换（从秒转毫秒）
      // 先将字符串转换为数字，然后判断是否需要从秒转毫秒
      let msgTime = Date.now()
      if (msg.time) {
          const timeNum = typeof msg.time === 'string' ? parseInt(msg.time, 10) : msg.time
          msgTime = timeNum > 1000000000000 ? timeNum : timeNum * 1000
      }
      messages.value.push({
        id: msg.id,
        serverMsgId: msg.serverMsgId,
        from: msg.from,
        to: msg.to,
        msg: msg.msg,
        time: msgTime,
        type: msg.type || 'txt',
        url: msg.url,
        isMe: true,
        isRead: true
      })

      if (sentId) {
        seenMessageIds.add(String(sentId))
      }
      console.log('[onMessageSent] add new ws message', {
        sentId,
        msg,
        messagesLenBefore: messages.value.length
      })
      
      // Update chat list if teacher（仅更新预览，不改变用户顺序）
      if (permissionType.value === 2 && activeChat.value) {
          activeChat.value.lastMessage = msg.type === 'img' ? '[图片]' : msg.msg
          activeChat.value.time = msgTime
      }
      
      scrollToBottom()
      applyLocaleTransform()
    }

    const onMessageRecalled = (e: any) => {
        console.log('打印撤回消息====', e)
        const detail = e.detail || {}
        let ext: any = detail.ext
        // ext 可能是字符串，需要先 JSON.parse 再取 msg_id
        if (typeof ext === 'string') {
          try {
            ext = JSON.parse(ext)
          } catch (err) {
            console.error('解析撤回消息 ext 失败：', ext, err)
            ext = null
          }
        }

        // 兼容多种字段来源：优先 ext.msg_id / msgId，其次 detail.msg_id / msgId / mid
        let recallId: any = null
        if (ext) {
          recallId = ext.msg_id || ext.msgId
        }
        if (!recallId) {
          recallId = detail.msg_id || detail.msgId || detail.mid
        }
        if (!recallId) return
        const recallIdStr = String(recallId)

        let targetMsg: ChatMessage | null = null

        if (permissionType.value === 2) {
          // 老师模式：优先在多会话消息池中查找对应会话的那条消息
          for (const [chatKey, list] of Object.entries(messageMap.value)) {
            const idx = list.findIndex(
              m =>
                (m.serverMsgId && String(m.serverMsgId) === recallIdStr) ||
                (m.id && String(m.id) === recallIdStr)
            )
            if (idx !== -1) {
              list[idx].isRecalled = true
              targetMsg = list[idx]
              // 撤回后刷新该会话的预览
              syncChatPreviewFromList(chatKey, list)
              // 如果当前正在查看这个会话，同步到 messages
              if (activeChat.value &&activeChat.value.suid === chatKey) {
                messages.value = list
              }
              break
            }
          }
          // 如果 messageMap 中没找到（例如还未写入该会话），兜底在当前 messages 列表中查找
          if (!targetMsg && messages.value.length) {
            const idx = messages.value.findIndex(
              m =>
                (m.serverMsgId && String(m.serverMsgId) === recallIdStr) ||
                (m.id && String(m.id) === recallIdStr)
            )
            if (idx !== -1) {
              messages.value[idx].isRecalled = true
              targetMsg = messages.value[idx]
            }
          }
        } else {
          // 学生模式：只有一个会话，在当前 messages 里查
          const idx = messages.value.findIndex(
            m =>
              (m.serverMsgId && String(m.serverMsgId) === recallIdStr) ||
              (m.id && String(m.id) === recallIdStr)
          )
          if (idx !== -1) {
            messages.value[idx].isRecalled = true
            targetMsg = messages.value[idx]
          }
        }

        if (targetMsg) {
          updateChatPreviewOnRecall(targetMsg)
        }
    }
    
    const onMessageRecalledSuccess = (e: any) => {
        const detail = e.detail || {}
        let ext: any = detail.ext
        // 同样先解析字符串 ext
        if (typeof ext === 'string') {
          try {
            ext = JSON.parse(ext)
          } catch (err) {
            console.error('解析撤回成功 ext 失败：', ext, err)
            ext = null
          }
        }

        // 兼容两种来源：
        // 1. 环信 SDK 撤回推送：e.detail.ext.msg_id / msgId
        // 2. 我们自己 recallMessage 成功回调：e.detail.mid
        let recallId: any = null
        if (ext) {
          recallId = ext.msg_id || ext.msgId
        }
        if (!recallId) {
          recallId = detail.msg_id || detail.msgId || detail.mid
        }
        if (!recallId) return
        const recallIdStr = String(recallId)

        let targetMsg: ChatMessage | null = null

        if (permissionType.value === 2) {
          // 老师模式：优先在多会话消息池中查找
          for (const [chatKey, list] of Object.entries(messageMap.value)) {
            const idx = list.findIndex(
              m =>
                (m.serverMsgId && String(m.serverMsgId) === recallIdStr) ||
                (m.id && String(m.id) === recallIdStr)
            )
            if (idx !== -1) {
              list[idx].isRecalled = true
              targetMsg = list[idx]
              syncChatPreviewFromList(chatKey, list)
              if (activeChat.value && activeChat.value.suid === chatKey) {
                messages.value = list
              }
              break
            }
          }
          // 如果 messageMap 中没找到，同样兜底在当前 messages 中查找
          if (!targetMsg && messages.value.length) {
            const idx = messages.value.findIndex(
              m =>
                (m.serverMsgId && String(m.serverMsgId) === recallIdStr) ||
                (m.id && String(m.id) === recallIdStr)
            )
            if (idx !== -1) {
              messages.value[idx].isRecalled = true
              targetMsg = messages.value[idx]
            }
          }
        } else {
          const idx = messages.value.findIndex(
            m =>
              (m.serverMsgId && String(m.serverMsgId) === recallIdStr) ||
              (m.id && String(m.id) === recallIdStr)
          )
          if (idx !== -1) {
            messages.value[idx].isRecalled = true
            targetMsg = messages.value[idx]
          }
        }

        if (targetMsg) {
          updateChatPreviewOnRecall(targetMsg)
        }
    }

    // 为不支持 vh/calc 的低版本浏览器设置固定高度
    const fixOldBrowserScroll = () => {
      const ua = navigator.userAgent
      // 检测不支持 vh 或 calc 的浏览器（IE8 及以下）
      const isOldBrowser = /MSIE [1-8]\./.test(ua) || /Trident\/[1-3]\./.test(ua)
      
      if (isOldBrowser) {
        const setHeights = () => {
          const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
          const sectionHeight = windowHeight
          
          // 设置容器高度
          const containerEl = document.querySelector('.chat-container') as HTMLElement
          const cardEl = document.querySelector('.chat-card') as HTMLElement
          const sectionEl = document.querySelector('.chat-section') as HTMLElement
          const wrapperEl = document.querySelector('.chat-wrapper') as HTMLElement
          
          if (containerEl) containerEl.style.height = windowHeight + 'px'
          if (cardEl) cardEl.style.height = windowHeight + 'px'
          if (sectionEl) sectionEl.style.height = sectionHeight + 'px'
          if (wrapperEl) wrapperEl.style.height = sectionHeight + 'px'
          
          // 设置 user-list 高度（仅老师模式）
          if (permissionType.value === 2) {
            const sidebarEl = document.querySelector('.sidebar') as HTMLElement
            const userListEl = document.querySelector('.user-list') as HTMLElement
            if (sidebarEl) sidebarEl.style.height = sectionHeight + 'px'
            if (userListEl) userListEl.style.height = (sectionHeight - 40) + 'px' // 减去 sidebar-header
          }
          
          // 设置 message-list 高度
          const messageListEl = messageListRef.value
          if (messageListEl) {
            messageListEl.style.height = (sectionHeight - 41 - 168) + 'px' // 减去 chat-header 和 input-area
          }
        }
        
        nextTick(() => {
          setHeights()
          window.addEventListener('resize', setHeights)
        })
      }
    }

    // 被踢下线事件处理
    const onKickedOffline = () => {
      console.log('User kicked offline')
      isKickedOffline.value = true
      isConnected.value = false
    }

    const handleOfflineRelogin = () => {
      // 关闭蒙层并触发客户端登录
      isKickedOffline.value = false
      try {
        // 和 FdIndex.vue 中保持一致
        window.open('gw://funccall?funcname=OnDoCommand')
      } catch (e) {
        console.error(e)
        // 兜底刷新页面
        window.location.reload()
      }
    }

    onMounted(() => {
      // 检测 Windows 版本
      const ua = navigator.userAgent
      // Windows NT 5.1 = XP, 5.2 = Server 2003, 6.0 = Vista
      const isVeryOldWindows = /Windows NT (5\.1|5\.2|6\.0)/.test(ua)
      // Windows NT 6.1 = Windows 7
      const isWindows7 = /Windows NT 6\.1/.test(ua)
      
      if (isVeryOldWindows) {
        // 非常老的系统（XP/Vista）完全禁用 emoji
        supportsEmoji.value = false
      } else if (isWindows7) {
        // Windows 7 使用图片替代 emoji 字符，确保正常显示
        supportsEmoji.value = true
        useEmojiImage.value = true
      }

      // 修复低版本浏览器的滚动问题
      fixOldBrowserScroll()

      fetchChatInfo().finally(() => applyLocaleTransform())
      window.addEventListener('click', closeContextMenu)
      window.addEventListener('on-im-message', onMessageReceived)
      window.addEventListener('on-im-message-sent', onMessageSent)
      window.addEventListener('on-im-message-recall', onMessageRecalled)
      window.addEventListener('on-im-message-recall-success', onMessageRecalledSuccess)
      window.addEventListener('on-im-kicked-offline', onKickedOffline)
      window.addEventListener('resize', handleWindowResize)
    })

    onUnmounted(() => {
      // 清除定时器
      if (contextMenuTimer.value) {
        clearTimeout(contextMenuTimer.value)
        contextMenuTimer.value = null
      }
      window.removeEventListener('click', closeContextMenu)
      window.removeEventListener('on-im-message', onMessageReceived)
      window.removeEventListener('on-im-message-sent', onMessageSent)
      window.removeEventListener('on-im-message-recall', onMessageRecalled)
      window.removeEventListener('on-im-message-recall-success', onMessageRecalledSuccess)
      window.removeEventListener('on-im-kicked-offline', onKickedOffline)
      window.removeEventListener('resize', handleWindowResize)
    })

    return {
      permissionType,
      username,
      currentUserName,
      inputText,
      messages,
      isConnected,
      initLoading,
      isKickedOffline,
      isActiveChat,
      getDisplayName,
      isTeacherMessage,
      handleSend,
      messageListRef,
      handleScroll,
      isLoadingHistory,
      shouldShowTime,
      formatTime,
      formatListTime,
      sending,
      supportsEmoji,
      useEmojiImage,
      emojiToImage,
      contextMenu,
      handleContextMenu,
      triggerRecall,
      showEmoji,
      emojiList,
      handleEmojiClick,
      handleImageUpload,
      onFileSelected,
      fileInput,
      chatList,
      activeChat,
      selectChat,
      parseEmoji,
      textareaRef,
      hasMoreHistory,
      offlineIcon,
      handleLoadMore,
      handleOfflineRelogin
    }
  }
})
